import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "kiipeilykolmio-lapselle-ikaopas";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Kiipeilykolmio lapselle — ikäopas vanhemmalle";
const DESC = "Kiipeilykolmio lapselle: ikäkohtaiset käyttöideat, turvavinkit ja milloin lisätä kaari tai ramppi. Vauvasta esikouluikäiseen.";
const PUBLISHED = "2026-06-18";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/kiipeilykolmio-lapselle-ikaopas")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Ikäopas"
      title={TITLE}
      lead="Yksi kiipeilykolmio palvelee koko varhaislapsuuden — kunhan tiedät, miten käyttää sitä eri ikävaiheissa. Tässä konkreettinen ikäopas vauvaikäisestä esikouluikäiseen."
      readingMinutes={7}
      publishedISO={PUBLISHED}
    >
      <h2>6–12 kuukautta — tutustumisvaihe</h2>
      <p>Vauva ei vielä kiipeä, mutta kolmio on hänelle <strong>tuettu seisontateline</strong>. Lattiamaton päälle pystytetty kolmio tarjoaa vakaat askelmat, joista vetää itsensä seisomaan. Tämä on iso motorinen virstanpylväs ja kolmio on siihen täydellinen.</p>
      <ul>
        <li>Aseta kolmio paksun pelimaton päälle.</li>
        <li>Älä koskaan nosta vauvaa askelmille — anna hänen ottaa askel itse, vaikka se kestäisi viikkoja.</li>
        <li>Peitä kolmio kankaalla → ”maja”, joka houkuttelee konttaamaan alle.</li>
      </ul>

      <h2>12–24 kuukautta — ensimmäiset kiipeämiset</h2>
      <p>Taapero alkaa nousta 1–2 alinta askelmaa. Tässä iässä alkaa myös <strong>riskien arvioinnin oppiminen</strong>: lapsi kokeilee, mistä uskaltaa laskeutua. Älä jännitä joka liikkeestä — istu lähellä, mutta anna prosessin tapahtua.</p>
      <p>Tämä on hyvä hetki lisätä <strong>ramppi</strong> kolmioon. Ryömi ylös → liu’u alas → nauru. Sama liike toistuu sata kertaa, ja jokaisella toistolla aivot rakentavat motoristista rataa.</p>

      <h2>2–3 vuotta — täysi käyttö</h2>
      <p>Lapsi kiipeää kärjelle, tasapainoilee yläaskelmalla ja kokeilee laskeutumista ”väärin päin”. Tässä iässä monet vanhemmat säikähtävät ja vievät kolmion pois — älä tee sitä. Lapsi <em>tarvitsee</em> tämän haasteen.</p>
      <ul>
        <li>Lisää <strong>kiipeilykaari</strong> — yhdessä kolmion kanssa saat pujottelu- ja keinuradan.</li>
        <li>Käytä tyynyjä, peittoja ja lakanoita majanrakennukseen.</li>
        <li>Keskity siihen, että lapsi <strong>nousee yhtä turvallisesti alas kuin ylös</strong> — tämä on Piklerin sääntö.</li>
      </ul>

      <h2>3–4 vuotta — yhdistelmäleikit</h2>
      <p>Lapsi alkaa keksiä reittejä itse: kolmiosta kaarelle, kaaren ympäri, takaisin rampille. Tämä on suunnittelua, ongelmanratkaisua ja sosiaalista neuvottelua kavereiden kanssa. Aikuinen vetäytyy taustalle.</p>

      <h2>4–6 vuotta — mielikuvitusleikki</h2>
      <p>Fyysinen haaste vähenee, mutta kolmio muuttuu <strong>näyttämöksi, linnaksi, laivaksi ja avaruusasemaksi</strong>. Esikouluikäinen rakentaa narratiiveja telineen ympärille — tämä on kielen ja luovan ajattelun kultakausi.</p>

      <h2>Pikataulu — milloin mitäkin?</h2>
      <table>
        <thead><tr><th>Ikä</th><th>Käyttö</th><th>Lisävaruste</th></tr></thead>
        <tbody>
          <tr><td>6–12 kk</td><td>Seisomatuki, maja</td><td>Pelimatto</td></tr>
          <tr><td>12–24 kk</td><td>Ensikiipeily, ryömi-liuku</td><td>+ Ramppi</td></tr>
          <tr><td>2–3 v</td><td>Täysi kiipeily</td><td>+ Kiipeilykaari</td></tr>
          <tr><td>3–4 v</td><td>Yhdistelmäreitit</td><td>—</td></tr>
          <tr><td>4–6 v</td><td>Mielikuvitusleikki</td><td>Lakana, tyynyt</td></tr>
        </tbody>
      </table>

      <p>Lue myös: <Link to="/montessori-koulu/pikler-kiipeilyteline-opas">Pikler-kiipeilyteline — täydellinen ostajan opas</Link>. Tai siirry suoraan kauppaan: <Link to="/kauppa" search={{ kategoria: "climbing" }}>Selaa kiipeilytelineitä →</Link></p>
    </BlogPost>
  );
}